import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../ui/use-toast';
import { Switch } from '../ui/switch';
import {
  Edit,
  Package,
  AlertCircle,
  Save,
  Building2,
  Tag,
  ImageIcon,
  Upload,
  X,
  Plus,
  Loader2,
  DollarSign
} from 'lucide-react';
import { SupplierProductsAPI } from '../../lib/api';
import { SupplierProduct, SupplierProductUpdateRequest } from '../../lib/types';

import {
  EnhancedDialog,
  EnhancedDialogContent,
  EnhancedDialogHeader,
  EnhancedDialogBody,
  EnhancedDialogFooter,
  EnhancedDialogTitle,
  EnhancedDialogDescription
} from '../ui/enhanced-dialog';
import { FormField, FormSection, FormGrid } from '../ui/form-field';
import { CurrencyInput } from '../ui/currency-input';
import { NumberInput } from '../ui/number-input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { StatusBadge } from '../ui/status-badge';

interface SupplierProductEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: SupplierProduct;
  onSuccess: () => void;
}

export default function SupplierProductEditModal({
  isOpen,
  onClose,
  product,
  onSuccess
}: SupplierProductEditModalProps) {
  // ALL HOOKS MUST BE DECLARED BEFORE ANY CONDITIONAL RETURNS

  // Form state - initialize with safe defaults
  const [formData, setFormData] = useState<SupplierProductUpdateRequest>({
    price: product?.price || 0,
    maxQuantity: product?.maxQuantity || 0,
    minOrderQuantity: product?.minOrderQuantity || 1,
    isActive: product?.isActive ?? true,
    adminNotes: '',
    productDetails: {
      name: product?.product?.name || '',
      description: product?.product?.description || '',
      unit: product?.product?.unit || '',
      basePrice: product?.product?.basePrice || 0,
      hsnCode: product?.product?.hsnCode || '',
      gstRate: product?.product?.gstRate || 18,
      specifications: product?.product?.specifications || []
    }
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'pricing' | 'product' | 'admin' | 'images'>('pricing');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Image management state
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [imageLoading, setImageLoading] = useState(false);

  // Local product state to track image updates
  const [currentProduct, setCurrentProduct] = useState(product);

  // Sync currentProduct with product prop when it changes
  useEffect(() => {
    if (isOpen && product) {
      setCurrentProduct(product);
    }
  }, [isOpen, product?._id]); // Only depend on isOpen and product ID, not the entire product object

  // Cleanup image URLs only when component unmounts
  useEffect(() => {
    return () => {
      // Clean up image preview URLs when component unmounts
      imagePreviewUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []); // Empty dependency array - only run on unmount

  // Reset modal state when closing
  const resetModalState = useCallback(() => {
    setSelectedImages([]);
    setImagePreviewUrls([]);
    setImagesToDelete([]);
    setImageLoading(false);
    setErrors({});
    setActiveTab('pricing');
  }, []);

  // Update form data - memoized to prevent unnecessary re-renders
  const updateFormData = useCallback((field: string, value: any) => {
    setFormData(prev => {
      if (field.startsWith('productDetails.')) {
        const detailField = field.replace('productDetails.', '');
        return {
          ...prev,
          productDetails: {
            ...prev.productDetails,
            [detailField]: value
          }
        };
      } else {
        return { ...prev, [field]: value };
      }
    });

    // Clear error when user starts typing
    setErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
      return prev;
    });
  }, []); // Empty dependency array since we're using functional updates

  // Handle image selection
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Validate file types and sizes
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not an image file`,
          variant: "destructive"
        });
        return false;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: "File too large",
          description: `${file.name} is larger than 5MB`,
          variant: "destructive"
        });
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setSelectedImages(prev => [...prev, ...validFiles]);

      // Create preview URLs
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreviewUrls(prev => [...prev, e.target?.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }

    // Reset input
    event.target.value = '';
  };

  // Remove new image
  const removeNewImage = (index: number) => {
    // Clean up the URL to prevent memory leaks
    const urlToRevoke = imagePreviewUrls[index];
    if (urlToRevoke && urlToRevoke.startsWith('blob:')) {
      URL.revokeObjectURL(urlToRevoke);
    }

    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  // Mark existing image for deletion
  const markImageForDeletion = (imageUrl: string) => {
    setImagesToDelete(prev => [...prev, imageUrl]);
  };

  // Unmark image for deletion
  const unmarkImageForDeletion = (imageUrl: string) => {
    setImagesToDelete(prev => prev.filter(url => url !== imageUrl));
  };

  // Handle image updates
  const handleImageUpdate = async () => {
    setImageLoading(true);
    try {
      const formData = new FormData();

      // Add images to delete
      if (imagesToDelete.length > 0) {
        formData.append('imagesToDelete', JSON.stringify(imagesToDelete));
      }

      // Add new images
      selectedImages.forEach(file => {
        formData.append('productImages', file);
      });

      const response = await SupplierProductsAPI.updateImages(product!._id, formData);

      if (response.success) {
        toast({
          title: "Success",
          description: "Product images updated successfully",
          variant: "default"
        });

        // Update the local product state with new images (if the API returns updated product)
        if (response.data.supplierProduct) {
          setCurrentProduct(response.data.supplierProduct);
        }

        // Reset image state
        setSelectedImages([]);
        setImagePreviewUrls([]);
        setImagesToDelete([]);

        // Don't call onSuccess() here - keep the modal open to show updated images
        // onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update images",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      console.error('Error updating images:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update images. Please try again.",
        variant: "destructive"
      });
    } finally {
      setImageLoading(false);
    }
  };

  // Handle form submission - memoized to prevent recreation
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (loading) return; // Prevent double submission

    setLoading(true);

    try {
      const response = await SupplierProductsAPI.fullUpdate(product._id, formData);

      if (response.success) {
        toast({
          title: "Success",
          description: "Product updated successfully",
          variant: "default"
        });

        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update product",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      console.error('Error updating product:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update product. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [product?._id, formData, loading, onSuccess]); // Dependencies for useCallback

  const handleClose = useCallback(() => {
    resetModalState();
    onClose();
  }, [resetModalState, onClose]);

  // Early return if product is not loaded - MUST be after all hooks
  if (!product) {
    return null;
  }

  return (
    <EnhancedDialog open={isOpen} onOpenChange={handleClose}>
      <EnhancedDialogContent size="4xl">
        <EnhancedDialogHeader>
          <EnhancedDialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Product - {product?.product?.name || 'Product'}
          </EnhancedDialogTitle>
          <EnhancedDialogDescription>
            Update product details, pricing, and supplier settings
          </EnhancedDialogDescription>
        </EnhancedDialogHeader>

        {/* Tab Navigation */}
        <div className="flex border-b px-6 bg-background">
          <button
            type="button"
            onClick={() => setActiveTab('pricing')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'pricing'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
          >
            <DollarSign className="h-4 w-4 inline mr-2" />
            Pricing & Quantity
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('product')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'product'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
          >
            <Package className="h-4 w-4 inline mr-2" />
            Product Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('admin')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'admin'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
          >
            <AlertCircle className="h-4 w-4 inline mr-2" />
            Admin Settings
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('images')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'images'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
          >
            <ImageIcon className="h-4 w-4 inline mr-2" />
            Images
          </button>
        </div>

        <EnhancedDialogBody>
          <form id="edit-product-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Product Information Header */}
            <FormSection title="Product Overview" icon={<Package className="h-5 w-5" />}>
              <div className="p-4">
                <div className="flex items-start gap-4">
                  {currentProduct?.product?.images?.length > 0 && (
                    <img
                      src={currentProduct.product.images.find(img => img.isDefault)?.url || currentProduct.product.images[0]?.url}
                      alt={currentProduct?.product?.name || 'Product'}
                      className="w-16 h-16 rounded-md object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{product?.product?.name || 'N/A'}</h3>
                    <p className="text-muted-foreground text-sm">{product?.product?.category?.name || 'Uncategorized'}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-sm">
                        <Building2 className="h-4 w-4 inline mr-1" />
                        {product?.supplier?.businessName || 'N/A'}
                      </span>
                      <span className="text-sm">
                        <Package className="h-4 w-4 inline mr-1" />
                        Current Stock: {product?.inventory?.availableStock ?? 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </FormSection>

            {/* Tab Content */}
            {activeTab === 'pricing' && (
              <FormSection
                title="Pricing & Quantity Settings"
                icon={<span className="text-lg">₹</span>}
              >
                <FormGrid columns={3}>
                  <FormField
                    label="Price (₹)"
                    required
                    hint={`Current: ₹${product.price.toLocaleString()}`}
                  >
                    <CurrencyInput
                      value={formData.price}
                      onChange={(value) => updateFormData('price', value)}
                      placeholder="0.00"
                      minValue={0}
                    />
                  </FormField>

                  <FormField
                    label="Min Order Quantity"
                    hint={`Current: ${product.minOrderQuantity}`}
                  >
                    <NumberInput
                      value={formData.minOrderQuantity}
                      onChange={(value) => updateFormData('minOrderQuantity', value)}
                      placeholder="1"
                      minValue={1}
                      allowDecimals={false}
                    />
                  </FormField>

                  <FormField
                    label="Max Order Quantity"
                    hint={`Current: ${product.maxQuantity}`}
                  >
                    <NumberInput
                      value={formData.maxQuantity}
                      onChange={(value) => updateFormData('maxQuantity', value)}
                      placeholder="1000"
                      minValue={1}
                      allowDecimals={false}
                    />
                  </FormField>
                </FormGrid>

                {/* Price Comparison */}
                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Price Impact Analysis</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Current Price:</span>
                      <p className="font-medium">₹{product.price.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">New Price:</span>
                      <p className="font-medium">₹{(formData.price || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Difference:</span>
                      <p className={`font-medium ${(formData.price || 0) > product.price ? 'text-red-600' :
                        (formData.price || 0) < product.price ? 'text-green-600' : 'text-muted-foreground'
                        }`}>
                        {((formData.price || 0) - product.price) > 0 ? '+' : ''}
                        ₹{((formData.price || 0) - product.price).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">% Change:</span>
                      <p className={`font-medium ${(formData.price || 0) > product.price ? 'text-red-600' :
                        (formData.price || 0) < product.price ? 'text-green-600' : 'text-muted-foreground'
                        }`}>
                        {product.price > 0 ?
                          (((formData.price || 0) - product.price) / product.price * 100).toFixed(2) + '%' :
                          '0%'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </FormSection>
            )}

            {activeTab === 'product' && (
              <FormSection
                title="Product Details"
                icon={<Tag className="h-5 w-5" />}
              >
                <FormGrid columns={2}>
                  <FormField label="Product Name">
                    <Input
                      value={formData.productDetails?.name || ''}
                      onChange={(e) => updateFormData('productDetails.name', e.target.value)}
                      placeholder="Enter product name"
                    />
                  </FormField>

                  <FormField label="Unit">
                    <Input
                      value={formData.productDetails?.unit || ''}
                      onChange={(e) => updateFormData('productDetails.unit', e.target.value)}
                      placeholder="e.g., kg, ton, piece"
                    />
                  </FormField>
                </FormGrid>

                <FormField label="Description">
                  <Textarea
                    value={formData.productDetails?.description || ''}
                    onChange={(e) => updateFormData('productDetails.description', e.target.value)}
                    placeholder="Enter detailed product description"
                    rows={3}
                  />
                </FormField>

                <FormGrid columns={3}>
                  <FormField label="Base Price (₹)">
                    <CurrencyInput
                      value={formData.productDetails?.basePrice || 0}
                      onChange={(value) => updateFormData('productDetails.basePrice', value)}
                      placeholder="0.00"
                      minValue={0}
                    />
                  </FormField>

                  <FormField label="HSN Code">
                    <Input
                      value={formData.productDetails?.hsnCode || ''}
                      onChange={(e) => updateFormData('productDetails.hsnCode', e.target.value)}
                      placeholder="Enter HSN code"
                    />
                  </FormField>

                  <FormField label="GST Rate (%)">
                    <Select
                      value={formData.productDetails?.gstRate?.toString() || "18"}
                      onValueChange={(value) => updateFormData('productDetails.gstRate', Number(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select GST rate" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5%</SelectItem>
                        <SelectItem value="12">12%</SelectItem>
                        <SelectItem value="18">18%</SelectItem>
                        <SelectItem value="28">28%</SelectItem>
                        <SelectItem value="0">0%</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormField>
                </FormGrid>
              </FormSection>
            )}

            {activeTab === 'admin' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Admin Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="isActive">Product Status</Label>
                      <p className="text-sm text-muted-foreground">
                        Control whether this product is visible to mobile users
                      </p>
                    </div>
                    <Switch
                      id="isActive"
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
                    />
                  </div>

                  <div>
                    <Label htmlFor="adminNotes">Admin Notes</Label>
                    <Textarea
                      id="adminNotes"
                      value={formData.adminNotes}
                      onChange={(e) => setFormData(prev => ({ ...prev, adminNotes: e.target.value }))}
                      placeholder="Add any administrative notes about this update..."
                      rows={3}
                    />
                  </div>

                  {/* Current Status Display */}
                  <div className="bg-muted p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Current Status</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Status:</span>
                        <p className="font-medium">
                          <StatusBadge status={product.isActive ? 'active' : 'inactive'} />
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Stock Level:</span>
                        <p className="font-medium">
                          {product?.inventory?.isOutOfStock ? (
                            <span className="text-red-600">Out of Stock</span>
                          ) : product?.inventory?.isLowStock ? (
                            <span className="text-orange-600">Low Stock</span>
                          ) : (
                            <span className="text-green-600">In Stock</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Last Updated:</span>
                        <p className="font-medium">
                          {new Date(product.updatedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Created:</span>
                        <p className="font-medium">
                          {new Date(product.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'images' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Product Images
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Current Images */}
                  <div>
                    <Label>Current Images ({currentProduct?.product?.images?.length || 0})</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-2">
                      {(currentProduct?.product?.images || []).map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image.url}
                            alt={`Product image ${index + 1}`}
                            className={`w-full h-32 object-cover rounded-lg border-2 ${image.isDefault ? 'border-orange-500' : 'border-gray-200'
                              } ${imagesToDelete.includes(image.url) ? 'opacity-50' : ''
                              }`}
                          />
                          {image.isDefault && (
                            <div className="absolute top-2 left-2 bg-orange-500 text-white text-xs px-2 py-1 rounded">
                              Default
                            </div>
                          )}
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {imagesToDelete.includes(image.url) ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                onClick={() => unmarkImageForDeletion(image.url)}
                                className="h-8 w-8 p-0"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                onClick={() => markImageForDeletion(image.url)}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          {imagesToDelete.includes(image.url) && (
                            <div className="absolute inset-0 bg-red-500 bg-opacity-20 rounded-lg flex items-center justify-center">
                              <span className="text-red-600 font-medium text-sm">Will be deleted</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {imagesToDelete.length > 0 && (
                      <p className="text-sm text-red-600 mt-2">
                        {imagesToDelete.length} image(s) marked for deletion
                      </p>
                    )}
                  </div>

                  {/* Add New Images */}
                  <div>
                    <Label>Add New Images</Label>
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <Input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageSelect}
                          className="hidden"
                          id="imageUpload"
                        />
                        <Label htmlFor="imageUpload" className="cursor-pointer">
                          <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-600">
                            Click to select images or drag and drop
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            PNG, JPG, GIF up to 5MB each
                          </p>
                        </Label>
                      </div>

                      {/* Preview New Images */}
                      {selectedImages.length > 0 && (
                        <div>
                          <Label>New Images ({selectedImages.length})</Label>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-2">
                            {imagePreviewUrls.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`New image ${index + 1}`}
                                  className="w-full h-32 object-cover rounded-lg border-2 border-green-200"
                                />
                                <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                                  New
                                </div>
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => removeNewImage(index)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Update Images Button */}
                      {(selectedImages.length > 0 || imagesToDelete.length > 0) && (
                        <Button
                          type="button"
                          onClick={handleImageUpdate}
                          disabled={imageLoading}
                          className="w-full"
                        >
                          {imageLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Updating Images...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Update Images ({selectedImages.length} new, {imagesToDelete.length} to delete)
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </form>
        </EnhancedDialogBody>

        <EnhancedDialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="edit-product-form" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </EnhancedDialogFooter>
      </EnhancedDialogContent>
    </EnhancedDialog>
  );
} 